// src/frontend/js/modern/core/light-queue.js

/**
 * Lightweight FIFO Queue with Concurrency Control
 * Drop-in replacement for p-queue covering only used features:
 * - Concurrency limit
 * - Auto-start
 * - Event emitting (add, idle, error)
 * - Size/Pending getters
 *
 * @param {Object} options
 * @param {number} [options.concurrency=5]
 * @param {boolean} [options.autoStart=true]
 */
export class LightQueue {
	constructor({ concurrency = 5, autoStart = true } = {}) {
		this._concurrency = Math.max(1, concurrency);
		this._autoStart = autoStart;
		this._pendingCount = 0;
		this._queue = [];
		this._isPaused = !autoStart;
		this._listeners = { add: [], idle: [], error: [] };
	}

	/**
	 * Add a promise-returning function to the queue.
	 * @param {Function} fn - Async function to execute
	 * @returns {Promise<any>} Resolves/rejects when the task completes
	 */
	add(fn) {
		return new Promise((resolve, reject) => {
			const task = { fn, resolve, reject };
			this._queue.push(task);
			this._emit('add');
			this._tryNext();
		});
	}

	/**
	 * Process next tasks if concurrency allows and queue is not paused.
	 * @private
	 */
	_tryNext() {
		if (this._isPaused) return;

		while (
			this._pendingCount < this._concurrency &&
			this._queue.length > 0
			) {
			const task = this._queue.shift();
			this._pendingCount++;

			Promise.resolve()
				.then(() => task.fn())
				.then(
					(result) => {
						this._pendingCount--;
						task.resolve(result);
						this._checkIdle();
						this._tryNext();
					},
					(error) => {
						this._pendingCount--;
						this._emit('error', error);
						task.reject(error);
						this._checkIdle();
						this._tryNext();
					}
				);
		}
	}

	/**
	 * Emit idle event when all work is done.
	 * @private
	 */
	_checkIdle() {
		if (this._pendingCount === 0 && this._queue.length === 0) {
			this._emit('idle');
		}
	}

	/**
	 * Simple internal event emitter.
	 * @private
	 */
	_emit(event, data) {
		const handlers = this._listeners[event];
		if (!handlers) return;
		for (let i = 0; i < handlers.length; i++) {
			try {
				handlers[i](data);
			} catch (e) {
				console.error(`[LightQueue] Handler error for "${event}":`, e);
			}
		}
	}

	/**
	 * Subscribe to queue events.
	 * @param {'add'|'idle'|'error'} event
	 * @param {Function} handler
	 */
	on(event, handler) {
		if (!this._listeners[event]) {
			this._listeners[event] = [];
		}
		this._listeners[event].push(handler);
	}

	/** Pause processing (queued items stay in buffer). */
	pause() {
		this._isPaused = true;
	}

	/** Resume processing. */
	start() {
		this._isPaused = false;
		this._tryNext();
	}

	/** Number of items waiting in queue (not yet started). */
	get size() {
		return this._queue.length;
	}

	/** Number of items currently executing. */
	get pending() {
		return this._pendingCount;
	}

	/** Clear all listeners and pending state. */
	clear() {
		this._queue = [];
		this._pendingCount = 0;
		this._listeners = { add: [], idle: [], error: [] };
	}
}
